import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Alert,
    LinearProgress,
    Avatar
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CloudDownload as DownloadIcon,
    Description as FileIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://65.1.61.204:3000/api';

const DataManagementPage = () => {
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleFileChange = (event) => {
        setUploadFile(event.target.files[0]);
        setUploadStatus(null);
    };

    const handleUpload = async () => {
        if (!uploadFile) return;

        const formData = new FormData();
        formData.append('file', uploadFile);

        setUploading(true);
        setUploadStatus(null);

        try {
            const response = await fetch(`${API_BASE_URL}/data/import/devices`, {
                method: 'POST',
                body: formData,
                // Content-Type header is not set manually so browser sets boundary
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            setUploadStatus({ type: 'success', message: 'Database updated successfully from Excel file.' });
            setUploadFile(null);
            document.getElementById('file-upload').value = '';
        } catch (error) {
            console.error('Upload error:', error);
            setUploadStatus({ type: 'error', message: 'Failed to upload data. Please ensure the format is correct.' });
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/data/export/devices`);

            if (!response.ok) {
                throw new Error('Download failed');
            }

            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `system_database_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download error:', error);
            setUploadStatus({ type: 'error', message: 'Failed to download database.' });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box p={3} className="animate-fade-in">
            {uploadStatus && (
                <Alert severity={uploadStatus.type} sx={{ mb: 3, borderRadius: '12px' }}>
                    {uploadStatus.message}
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Download Section */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.50', color: 'primary.main', mb: 3 }}>
                                <DownloadIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Download Database</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 300 }}>
                                Export the entire system database (Devices, Users, Logs) as an Excel (.xlsx) file for backup or reporting.
                            </Typography>

                            <Box mt="auto">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={downloading ? null : <FileIcon />}
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    sx={{ borderRadius: '14px', px: 4, py: 1.5, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                                >
                                    {downloading ? 'Downloading...' : 'Export to Excel'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Upload Section */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Avatar sx={{ width: 80, height: 80, bgcolor: 'success.50', color: 'success.main', mb: 3 }}>
                                <UploadIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Update Database</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 300 }}>
                                Upload an updated Excel file to modify device records or add new assets in bulk.
                            </Typography>

                            <Box mt="auto" width="100%" maxWidth={300}>
                                <input
                                    accept=".xlsx, .xls"
                                    style={{ display: 'none' }}
                                    id="file-upload"
                                    type="file"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="file-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        fullWidth
                                        sx={{ mb: 2, borderRadius: '12px', borderColor: 'divider', color: 'text.secondary', py: 1.5, justifyContent: 'flex-start', px: 2 }}
                                        startIcon={<FileIcon />}
                                    >
                                        {uploadFile ? uploadFile.name : 'Select Excel File...'}
                                    </Button>
                                </label>

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    onClick={handleUpload}
                                    disabled={!uploadFile || uploading}
                                    className="gradient-primary"
                                    sx={{ borderRadius: '14px', py: 1.5, boxShadow: 'var(--shadow-md)' }}
                                >
                                    {uploading ? 'Uploading...' : 'Upload & Update'}
                                </Button>
                                {uploading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DataManagementPage;
